import { trapFocus, removeTrapFocus, lockScroll, unlockScroll, closeDrawer, setDrawerOpenAccessibility, setDrawerClosedAccessibility, setInteractionDisabled } from '@theme/theme-global';
import { BagLines } from '@theme/bag-lines';

class BagDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this._focusTimer = null;
    this._onTransitionEnd = this._onTransitionEnd.bind(this);
    this._onEscapeKeydown = this._onEscapeKeydown.bind(this);

    this.bindHeaderTriggers();
    this.bindCloseTriggers();
  }

  connectedCallback() {
    this.bindRecommendedTrigger();
    this.syncRecommendedAccessibility();
  }

  disconnectedCallback() {
    if (this._recommendedTriggerHandler) {
      this.removeEventListener('click', this._recommendedTriggerHandler);
    }
    this.clearPendingFocus();
    document.removeEventListener('keydown', this._onEscapeKeydown);
  }

  get closeButton() {
    return (
      this.querySelector('.cart-drawer-drawer-header .drawer-close-btn') ||
      this.querySelector('.drawer-content .drawer-close-btn')
    );
  }

  bindRecommendedTrigger() {
    this._recommendedTriggerHandler = (event) => {
      const closeBtn = event.target.closest('[data-cart-drawer-recommended-close]');
      if (closeBtn && this.contains(closeBtn)) {
        event.preventDefault();
        event.stopPropagation();
        this.toggleRecommended(false);
        this.closeButton?.focus();
        return;
      }

      const trigger = event.target.closest('[data-cart-drawer-recommended-trigger]');
      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        this.toggleRecommended();
      }
    };
    this.addEventListener('click', this._recommendedTriggerHandler);
  }

  syncRecommendedAccessibility() {
    const sidebar = this.querySelector('[data-cart-drawer-recommended-sidebar]');
    if (!sidebar) return;
    const isVisible = sidebar.classList.contains('is-visible');
    setInteractionDisabled(sidebar, !isVisible);
  }

  toggleRecommended(isOpen) {
    const sidebar = this.querySelector('[data-cart-drawer-recommended-sidebar]');
    if (!sidebar) return;

    if (isOpen !== undefined) {
      sidebar.classList.toggle('is-visible', isOpen);
    } else {
      sidebar.classList.toggle('is-visible');
    }
    this.syncRecommendedAccessibility();
  }

  bindHeaderTriggers() {
    this._cartTriggers = Array.from(
      document.querySelectorAll('[aria-controls="CartDrawer"]:not([data-sticky-view-cart])'),
    );
    if (!this._cartTriggers.length) return;

    const supportsHover = window.matchMedia('(hover: hover)').matches;

    this._handleTriggerClick = (event) => {
      event.preventDefault();
      this.open(event.currentTarget);
    };

    this._handleHoverPreventClick = (event) => {
      event.preventDefault();
    };

    this._handleMouseEnter = (event) => {
      const trigger = event.currentTarget;
      this._hoverTimer = setTimeout(() => {
        this.open(trigger);
      }, 300);
    };

    this._handleMouseLeave = () => {
      clearTimeout(this._hoverTimer);
    };

    this._handleTriggerKeydown = (event) => {
      if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
        event.preventDefault();
        this.open(event.currentTarget);
      }
    };

    this._cartTriggers.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      const triggerType = cartLink.dataset.openDrawer || 'click';
      if (triggerType === 'hover' && supportsHover) {
        cartLink.addEventListener('click', this._handleHoverPreventClick);
        cartLink.addEventListener('mouseenter', this._handleMouseEnter);
        cartLink.addEventListener('mouseleave', this._handleMouseLeave);
      } else {
        cartLink.addEventListener('click', this._handleTriggerClick);
      }
      cartLink.addEventListener('keydown', this._handleTriggerKeydown);
    });
  }

  setTriggerExpanded(expanded) {
    this._cartTriggers?.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  clearPendingFocus() {
    clearTimeout(this._focusTimer);
    this._focusTimer = null;
    this.removeEventListener('transitionend', this._onTransitionEnd);
  }

  _onTransitionEnd(event) {
    if (event.target !== this && !event.target?.classList?.contains('drawer-inner')) return;
    this.clearPendingFocus();
    this.trapDrawerFocus();
  }

  _onEscapeKeydown(event) {
    if (event.code !== 'Escape') return;
    if (!this.hasAttribute('open') && !this.hasAttribute('active')) return;
    event.preventDefault();
    this.close();
  }

  trapDrawerFocus() {
    if (!this.hasAttribute('active')) return;

    const focusElement = this.closeButton || this;
    trapFocus(this, focusElement);
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.rememberFocus(triggeredBy);
    } else if (!this.activeElement) {
      this.rememberFocus(document.querySelector('[aria-controls="CartDrawer"]:not([data-sticky-view-cart])'));
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.bindNoteSummary(cartDrawerNote);

    setDrawerOpenAccessibility(this);
    this.syncRecommendedAccessibility();

    this.clearPendingFocus();

    setTimeout(() => {
      this.setAttribute('open', '');
      this.setAttribute('active', '');
      requestAnimationFrame(() => {
        this.querySelectorAll('[is="cart-deals-products"]').forEach((deal) => {
          deal.slider?.update?.();
        });
      });
    });

    this.setTriggerExpanded(true);
    document.addEventListener('keydown', this._onEscapeKeydown);

    this.addEventListener('transitionend', this._onTransitionEnd);
    this._focusTimer = setTimeout(() => {
      this.clearPendingFocus();
      this.trapDrawerFocus();
    }, 400);

    lockScroll();
  }

  bindCloseTriggers() {
    this.querySelectorAll('[is="close-drawer"]').forEach((trigger) => {
      if (trigger.dataset.closeBound === 'true') return;
      trigger.dataset.closeBound = 'true';
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      });
    });
  }

  close() {
    if (!this.hasAttribute('open') && !this.hasAttribute('active')) return;

    this.clearPendingFocus();
    this.toggleRecommended(false);
    closeDrawer(this);
    removeTrapFocus(this.activeElement);
    setDrawerClosedAccessibility(this);
    unlockScroll();
    this.setTriggerExpanded(false);
    document.removeEventListener('keydown', this._onEscapeKeydown);
  }

  bindNoteSummary(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
  }

  applySectionHtml(parsedState, options = {}) {
    this.querySelector('.drawer-inner').classList.contains('is-empty') &&
      this.querySelector('.drawer-inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.sectionsToRefresh().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      const sectionHtml = this.extractSectionHtml(parsedState.sections[section.id], section.selector);
      if (sectionHtml === null) return;
      sectionElement.innerHTML = sectionHtml;
    });

    setTimeout(() => {
      this.bindCloseTriggers();
      this.syncRecommendedAccessibility();
      if (!options.skipOpen) this.open();
    });
  }

  extractSectionHtml(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  sectionsToRefresh() {
    return [
      {
        id: 'cart-count',
      },
      {
        id: 'cart-drawer',
        selector: '[data-shipping-box]',
      },
      {
        id: 'cart-drawer',
        selector: '[data-cart-saved-line="cart-drawer"]',
      },
      {
        id: 'cart-drawer',
        selector: '[bag-drawer-lines]',
      },
      {
        id: 'cart-drawer',
        selector: '[bag-drawer-footer]',
      },
      {
        id: 'cart-drawer',
        selector: '[data-cart-drawer-recommended-products]',
      },
    ];
  }

  parseSectionNode(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  rememberFocus(element) {
    this.activeElement = element;
  }
}

customElements.define('bag-drawer', BagDrawer);

class BagDrawerLines extends BagLines {
  sectionsToRefresh() {
    return [
      {
        id: 'cart-count',
        section: 'cart-count',
        selector: '.shopify-section',
      },
      {
        id: 'free-shipping-box',
        section: 'cart-drawer',
        selector: '[data-shipping-box]',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '[data-cart-saved-line="cart-drawer"]',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '[bag-drawer-lines]',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '[bag-drawer-footer]',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '[data-cart-drawer-recommended-products]',
      },
    ];
  }
}

customElements.define('bag-drawer-lines', BagDrawerLines);

class ExpandableProductCard extends HTMLElement {
  constructor() {
    super();
    this.toggleButton = this.querySelector('button[data-expandable-product-toggle]');
    this.panel = this.querySelector('div[data-expandable-product-panel]');
    this.toggleButton?.addEventListener('click', this.togglePanel.bind(this));
  }

  togglePanel() {
    const isExpanded = this.classList.toggle('is-expanded');
    this.toggleButton?.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    this.panel?.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');
  }
}
if (!customElements.get('expandable-product-card')) {
  customElements.define('expandable-product-card', ExpandableProductCard);
}
